// src/screens/VerificationPage.js
import { useState, useEffect, useRef } from "react";
import { Typography, Button, Paper, Box, Stepper, Step, StepLabel, StepConnector, IconButton, Alert, Snackbar } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useApp } from "../../context/AppContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../api/api";

import "../../assets/style/Verification.css";

// Импортируем иллюстрации
import idIllustration from "../../assets/img/verifiction-step-img.svg";

// 1. Определяем шаги верификации
const steps = [
  'personalInformation',
  'documentUpload', 
  'verificationComplete'
];

// 2. Контент для каждого шага
const stepContent = [
  {
    title: 'personalInformation',
    subtitle: 'personalInformationDesc',
    buttonText: 'nextStep',
    illustration: idIllustration
  },
  {
    title: 'documentUpload',
    subtitle: 'documentUploadDesc',
    buttonText: 'uploadDocuments',
    illustration: idIllustration
  },
  {
    title: 'completeKycVerification',
    subtitle: 'completeKycVerificationDesc',
    buttonText: 'startKycVerification',
    illustration: idIllustration
  }
];

// 3. Обновляем иконку, чтобы она показывала галочку для пройденных шагов
function StepIconComponent(props) {
  const { active, completed, icon } = props;

  if (completed) {
    return <div className="step-icon-container completed"><CheckIcon className="step-icon-checkmark" /></div>;
  }

  return (
    <div className={`step-icon-container ${active ? 'active' : ''}`}>
      {icon}
    </div>
  );
}

export default function Verification() { 
  const [activeStep, setActiveStep] = useState(0); 
  const [driverLicenseFile, setDriverLicenseFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userData, setUserData] = useState(null);
  const driverLicenseRef = useRef();
  const selfieRef = useRef();
  const { fetchUser, user: contextUser } = useApp();
  const { t } = useLanguage();

  // Переведенные шаги для динамического отображения
  const translatedSteps = steps.map(step => t(step));
  
  // Переведенный контент для каждого шага
  const translatedStepContent = stepContent.map(content => ({
    title: t(content.title),
    subtitle: t(content.subtitle),
    buttonText: t(content.buttonText),
    illustration: content.illustration
  }));

  useEffect(() => {
    const getActiveState = async () => {
      try {
        const userData = await fetchUser();
        
        if (userData) {
          setUserData(userData);
          
          // Проверяем, верифицирован ли пользователь
          if (userData.is_verif === 1) {
            // Если пользователь верифицирован, переходим на 3-й шаг
            setActiveStep(2);
          } else if (userData.verif_step !== undefined) {
            // Иначе используем сохраненный шаг
            setActiveStep(Number(userData.verif_step));
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    // Делаем запрос при каждом переходе на страницу
    getActiveState();
  }, []); // Запускаем только один раз при монтировании

  // Используем данные из контекста, если локальные данные не загружены
  useEffect(() => {
    if (!userData && contextUser) {
      setUserData(contextUser);
      
      // Проверяем, верифицирован ли пользователь из контекста
      if (contextUser.is_verif === 1) {
        setActiveStep(2);
      } else if (contextUser.verif_step !== undefined) {
        setActiveStep(Number(contextUser.verif_step));
      }
    }
  }, [userData, contextUser]);

  // Функции для обработки загрузки файлов
  const handleDriverLicenseChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setDriverLicenseFile(file);
      } else {
        setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      }
    }
  };

  const handleSelfieChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelfieFile(file);
      } else {
        setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      }
    }
  };

  // Функция для конвертации файла в base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Убираем префикс "data:image/jpeg;base64," и оставляем только base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleUploadDocuments = async () => {
    // Проверяем, есть ли новые файлы для загрузки
    if (!driverLicenseFile && !selfieFile) {
      setSnackbar({ open: true, message: 'Please select at least one document to upload', severity: 'error' });
      return;
    }

    // Если документы уже загружены и нет новых файлов, показываем сообщение
    if (userData && userData.doc && userData.selfie && !driverLicenseFile && !selfieFile) {
      setSnackbar({ open: true, message: 'Documents are already uploaded', severity: 'info' });
      return;
    }

    setIsUploading(true);
    try {
      // Подготавливаем данные для отправки
      const updateData = {};
      
      // Конвертируем новые файлы в base64, если они есть
      if (driverLicenseFile) {
        const docBase64 = await convertFileToBase64(driverLicenseFile);
        updateData.doc = docBase64;
      }
      
      if (selfieFile) {
        const selfieBase64 = await convertFileToBase64(selfieFile);
        updateData.selfie = selfieBase64;
      }

      // Получаем токен из sessionStorage
      const token = sessionStorage.getItem('accessToken');
      
      if (!token) {
        setSnackbar({ 
          open: true, 
          message: 'Authentication token not found. Please login again.', 
          severity: 'error' 
        });
        return;
      }
      
      // Отправляем данные на бэк
      const response = await api.put('/auth/profile', updateData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        setSnackbar({ open: true, message: 'Documents uploaded successfully!', severity: 'success' });
        setDriverLicenseFile(null);
        setSelfieFile(null);
        // Очищаем input поля
        if (driverLicenseRef.current) driverLicenseRef.current.value = '';
        if (selfieRef.current) selfieRef.current.value = '';
        
        // Обновляем данные пользователя
        try {
          const updatedUserData = await fetchUser();
          if (updatedUserData) {
            setUserData(updatedUserData);
          }
        } catch (err) {
          console.log('Error updating user data:', err);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Upload failed. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 5. Добавляем обработчик для кнопки (для перехода на следующий шаг)
  const handleNext = () => {
    // Если пользователь верифицирован, переходим на 3-й шаг
    if (userData && userData.is_verif === 1) {
      setActiveStep(2);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  return (
    <Paper elevation={0} className="verification-page-container" sx={{backgroundColor:"rgb(17, 25, 39)", borderRadius:"16px"}}>
      
      {/* Верхняя часть: Заголовок и подзаголовок */}
      <div className="verification-header">
        <Typography variant="h5" className="verification-title">{t('verification')}</Typography>
        <Typography variant="body1" className="verification-subtitle">
          {t('verifyAccountKyc')}
        </Typography>
      </div>

      {/* Степпер */}
     <Box className="stepper-container">
        {/* 6. Передаем АКТУАЛЬНЫЙ activeStep в Степпер */}
        <Stepper alternativeLabel activeStep={activeStep} connector={<StepConnector className="custom-step-connector" />}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel 
                StepIconComponent={StepIconComponent}
                label={t(label)}
                classes={{ label: 'custom-step-label' }}
              >
                {t(label)}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Центральная часть контента */}
      <div className="verification-content">
        {activeStep === 1 ? (
          <>
            <Typography variant="h4" className="content-title">
              {translatedStepContent[activeStep].title}
            </Typography>
            <Typography variant="body1" className="content-subtitle">
              {translatedStepContent[activeStep].subtitle}
            </Typography>
            {/* Вернули JSX для загрузки документов */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 3, 
              width: '100%', 
              maxWidth: 600, 
              mx: 'auto',
              mt: 3 
            }}>
              {/* Инпут для водительских прав */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                p: 3,
                border: '2px dashed rgba(99, 102, 241, 0.3)',
                borderRadius: 2,
                backgroundColor: 'rgba(99, 102, 241, 0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(99, 102, 241, 0.6)',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                }
              }}>
                <input
                  ref={driverLicenseRef}
                  type="file"
                  accept="image/*"
                  onChange={handleDriverLicenseChange}
                  style={{ display: 'none' }}
                />
                <IconButton
                  onClick={() => driverLicenseRef.current?.click()}
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    border: '2px solid rgba(99, 102, 241, 0.3)',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                      borderColor: 'rgba(99, 102, 241, 0.6)',
                    }
                  }}
                >
                  <CreditCardIcon sx={{ fontSize: 40, color: '#6366f1' }} />
                </IconButton>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, textAlign: 'center' }}>
                  {t('driverLicense')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mb: 2 }}>
                  {t('driverLicenseDesc')}
                </Typography>
                {userData && userData.doc ? (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <img 
                      src={`data:image/jpeg;base64,${userData.doc.replace(/^data:image\/[a-z]+;base64,/, '')}`}
                      alt="Driver License"
                      style={{
                        width: '100%',
                        maxWidth: '200px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #22c55e',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#ef4444', 
                        fontWeight: 'bold', 
                        display: 'none',
                        mt: 1 
                      }}
                    >
                      ⚠️ Image loading error
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 'bold', display: 'block', mt: 1 }}>
                      ✓ {t('alreadyUploaded')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5 }}>
                      {t('clickToReplace')}
                    </Typography>
                  </Box>
                ) : driverLicenseFile && (
                  <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 'bold' }}>
                    ✓ {driverLicenseFile.name}
                  </Typography>
                )}
              </Box>

              {/* Инпут для селфи */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                p: 3,
                border: '2px dashed rgba(99, 102, 241, 0.3)',
                borderRadius: 2,
                backgroundColor: 'rgba(99, 102, 241, 0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(99, 102, 241, 0.6)',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                }
              }}>
                <input
                  ref={selfieRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSelfieChange}
                  style={{ display: 'none' }}
                />
                <IconButton
                  onClick={() => selfieRef.current?.click()}
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    border: '2px solid rgba(99, 102, 241, 0.3)',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                      borderColor: 'rgba(99, 102, 241, 0.6)',
                    }
                  }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 40, color: '#6366f1' }} />
                </IconButton>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, textAlign: 'center' }}>
                  {t('selfie')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mb: 2 }}>
                  {t('selfieDesc')}
                </Typography>
                {userData && userData.selfie ? (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <img 
                      src={`data:image/jpeg;base64,${userData.selfie.replace(/^data:image\/[a-z]+;base64,/, '')}`}
                      alt="Selfie"
                      style={{
                        width: '100%',
                        maxWidth: '200px',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #22c55e',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#ef4444', 
                        fontWeight: 'bold', 
                        display: 'none',
                        mt: 1 
                      }}
                    >
                      ⚠️ Image loading error
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 'bold', display: 'block', mt: 1 }}>
                      ✓ {t('alreadyUploaded')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5 }}>
                      {t('clickToReplace')}
                    </Typography>
                  </Box>
                ) : selfieFile && (
                  <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 'bold' }}>
                    ✓ {selfieFile.name}
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Кнопка загрузки */}
            <Button 
              variant="contained" 
              size="large" 
              className="action-button"
              onClick={handleUploadDocuments}
              disabled={isUploading || (!driverLicenseFile && !selfieFile && userData && userData.doc && userData.selfie)}
              sx={{
                mt: 4,
                backgroundColor: '#6366f1',
                '&:hover': {
                  backgroundColor: '#4f46e5',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              {isUploading ? t('uploading') : (
                userData && userData.doc && userData.selfie && !driverLicenseFile && !selfieFile ? 
                t('documentsAlreadyUploaded') : 
                (driverLicenseFile || selfieFile ? t('uploadSelectedDocuments') : t('uploadBothDocuments'))
              )}
            </Button>
          </>
        ) : (
          <>
            <img 
              src={translatedStepContent[activeStep].illustration} 
              alt={translatedStepContent[activeStep].title} 
              className="content-illustration" 
            />
            <Typography variant="h4" className="content-title">
              {translatedStepContent[activeStep].title}
            </Typography>
            <Typography variant="body1" className="content-subtitle">
              {translatedStepContent[activeStep].subtitle}
            </Typography>
            {activeStep !== 2 && (
              <Button 
                variant="contained" 
                size="large" 
                className="action-button"
                onClick={handleNext}
                sx={{
                  mt: 3,
                  '&:hover': {
                    backgroundColor: '#4f46e5',
                  },
                  '&:active': {
                    backgroundColor: '#4338ca',
                  }
                }}
              >
                {translatedStepContent[activeStep].buttonText}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Paper>
  );
}